import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import GameOptions from "./GameOptions"; // Assumes in same directory or adjust path
import { useGameBot } from "./useGameBot"; // Assumes in same directory or adjust path
import PlayerNameInput from "./PlayerNameInput";

export const ChompGame = ({ onBack }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(5);
  const [cols, setCols] = useState(7);
  const [board, setBoard] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);

  // New State for Bot
  const [gameMode, setGameMode] = useState("PvP");
  const [difficulty, setDifficulty] = useState("Easy");
  
  // Player Names
  const [player1Name, setPlayer1Name] = useState("Player 1");
  const [player2Name, setPlayer2Name] = useState("Player 2");

  // Initialize board when game starts
  const startGame = () => {
    // Validate names if needed, but default is fine
    const newBoard = Array(rows)
      .fill()
      .map(() => Array(cols).fill(true));
    setBoard(newBoard);
    setGameStarted(true);
    setCurrentPlayer(1);
    setGameOver(false);
    setWinner(null);
  };

  // Check if only poison cookie remains
  const checkGameOver = (newBoard) => {
    let cookieCount = 0;
    for (let i = 0; i < newBoard.length; i++) {
      for (let j = 0; j < newBoard[i].length; j++) {
        if (newBoard[i][j]) cookieCount++;
      }
    }
    return cookieCount === 1 && newBoard[rows - 1][0]; // Only poison cookie left
  };

  // Core move logic extracted for reuse
  const makeMove = useCallback(
    ({ row, col }) => {
      // Check if clicking poison cookie (bottom-left)
      if (row === rows - 1 && col === 0) {
        setGameOver(true);
        setWinner(currentPlayer === 1 ? 2 : 1); // Current player loses
        return;
      }

      const newBoard = board.map((r) => [...r]);

      // Remove clicked cookie and all cookies above and to the right
      for (let i = 0; i <= row; i++) {
        for (let j = col; j < cols; j++) {
          newBoard[i][j] = false;
        }
      }

      setBoard(newBoard);

      // Check if game is over (only poison cookie left)
      if (checkGameOver(newBoard)) {
        setGameOver(true);
        setWinner(currentPlayer === 1 ? 1 : 2); // Next player will be forced to take poison
      } else {
        setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
      }
    },
    [board, rows, cols, currentPlayer]
  );

  // Bot Strategy Function
  const getBotMove = useCallback(
    (diff, currentGameState) => {
      const { board, rows, cols } = currentGameState;
      const validMoves = [];

      // Find all valid moves
      for (let i = 0; i < rows; i++) {
        for (let j = 0; j < cols; j++) {
          // If it's a cookie (true) and NOT the poison one (bottom-left)
          if (board[i][j] && !(i === rows - 1 && j === 0)) {
            validMoves.push({ row: i, col: j });
          }
        }
      }

      if (validMoves.length === 0) return null; // Should not happen if game not over

      // Easy: Random Move
      if (diff === "Easy") {
        const randomIndex = Math.floor(Math.random() * validMoves.length);
        return validMoves[randomIndex];
      }

      // Medium: Heuristic - Try not to leave 1x1 (winning state for opponent)
      if (diff === "Medium") {
        // Check for winning move
        for (let move of validMoves) {
          // Simulate move
          let testBoard = board.map((r) => [...r]);
          for (let i = 0; i <= move.row; i++) {
            for (let j = move.col; j < cols; j++) {
              testBoard[i][j] = false;
            }
          }

          let cookieCount = 0;
          for (let i = 0; i < rows; i++) {
            for (let j = 0; j < cols; j++) {
              if (testBoard[i][j]) cookieCount++;
            }
          }
          // If result leaves 1 cookie (poison), we win immediately
          if (cookieCount === 1) return move;
        }

        // If no immediate win, pick random
        const randomIndex = Math.floor(Math.random() * validMoves.length);
        return validMoves[randomIndex];
      }

      // Hard: Same for now
      if (diff === "Hard") {
        // 1. Check for immediate win
        for (let move of validMoves) {
          let testBoard = board.map((r) => [...r]);
          for (let i = 0; i <= move.row; i++) {
            for (let j = move.col; j < cols; j++) {
              testBoard[i][j] = false;
            }
          }
          let cookieCount = 0;
          for (let i = 0; i < rows; i++) {
            for (let j = 0; j < cols; j++) {
              if (testBoard[i][j]) cookieCount++;
            }
          }
          if (cookieCount === 1) return move;
        }

        // 2. Play safely (Random for now)
        const randomIndex = Math.floor(Math.random() * validMoves.length);
        return validMoves[randomIndex];
      }

      return validMoves[0];
    },
    []
  );

  // Hook for Bot
  const { isBotThinking } = useGameBot({
    gameMode,
    makeMove,
    playerTurn: currentPlayer,
    gameStarted,
    gameOver,
    botStrategy: getBotMove,
    gameState: { board, rows, cols },
    difficulty,
  });

  // Handle cookie click
  const handleCookieClick = (clickedRow, clickedCol) => {
    if (
      !gameStarted ||
      gameOver ||
      !board[clickedRow][clickedCol] ||
      isBotThinking
    ) {
      return;
    }
    // Prevent clicking during bot turn if PvP
    if (gameMode === "PvC" && currentPlayer === 2) return;

    makeMove({ row: clickedRow, col: clickedCol });
  };

  // Reset game
  const resetGame = () => {
    setGameStarted(false);
    setBoard([]);
    setCurrentPlayer(1);
    setGameOver(false);
    setWinner(null);
  };
  
  const getPlayerName = (id) => {
      if (gameMode === "PvC" && id === 2) return "Computer";
      return id === 1 ? player1Name : player2Name;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <button
          onClick={() => (onBack ? onBack() : navigate("/games"))}
          className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
        >
          ← Back to Games
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">
          Game of Chomp
        </h1>
        <p className="text-center text-gray-600 mb-8">
          Strategic cookie chomping - don't get stuck with the poison!
        </p>

        {!gameStarted ? (
          <div className="text-center">
            {/* Game Options Reused Component */}
            <GameOptions
              gameMode={gameMode}
              setGameMode={setGameMode}
              difficulty={difficulty}
              setDifficulty={setDifficulty}
              onStart={startGame}
            />
            
            <PlayerNameInput
                player1Name={player1Name}
                setPlayer1Name={setPlayer1Name}
                player2Name={player2Name}
                setPlayer2Name={setPlayer2Name}
                gameMode={gameMode}
            />

            <div className="mb-6 space-y-4 max-w-lg mx-auto border-t pt-6">
              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Board Size (Overrides Defaults):
                </label>
                <div className="flex justify-center items-center space-x-4">
                  <div>
                    <label className="block text-sm text-gray-600">Rows:</label>
                    <input
                      type="number"
                      min="3"
                      max="8"
                      value={rows}
                      onChange={(e) =>
                        setRows(
                          Math.min(
                            8,
                            Math.max(3, parseInt(e.target.value) || 5)
                          )
                        )
                      }
                      className="px-3 py-2 text-lg border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none w-20"
                    />
                  </div>
                  <span className="text-2xl">×</span>
                  <div>
                    <label className="block text-sm text-gray-600">
                      Columns:
                    </label>
                    <input
                      type="number"
                      min="3"
                      max="10"
                      value={cols}
                      onChange={(e) =>
                        setCols(
                          Math.min(
                            10,
                            Math.max(3, parseInt(e.target.value) || 7)
                          )
                        )
                      }
                      className="px-3 py-2 text-lg border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none w-20"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 text-gray-600 max-w-2xl mx-auto">
              <h3 className="font-semibold mb-4 text-lg">Game Rules:</h3>
              <div className="bg-gray-50 p-4 rounded-lg text-left space-y-2">
                <p>• Players take turns clicking on cookies</p>
                <p>
                  • Clicking a cookie removes it and all cookies above and to
                  the right
                </p>
                <p>• The cookie in the bottom-left corner is poison (💀)</p>
                <p>• The player forced to take the poison cookie loses</p>
                <p>
                  • Strategy tip: Try to force your opponent into a bad
                  position!
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div className="text-xl font-semibold">
                {gameOver ? (
                  <span className="text-green-600">
                    🎉 {getPlayerName(winner)} Wins!
                  </span>
                ) : (
                  <span>
                    Current Player:
                    <span
                      className={
                        currentPlayer === 1
                          ? "text-blue-600 ml-2"
                          : "text-red-600 ml-2"
                      }
                    >
                      {getPlayerName(currentPlayer)}
                    </span>
                    {isBotThinking && <span className="ml-2 text-sm text-gray-500">(Thinking...)</span>}
                  </span>
                )}
              </div>
              <button
                onClick={resetGame}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                New Game
              </button>
            </div>

            <div className="flex justify-center mb-4">
              <div className="inline-block bg-yellow-50 p-4 rounded-lg border-2 border-yellow-200">
                {board.map((row, rowIndex) => (
                  <div key={rowIndex} className="flex">
                    {row.map((cookie, colIndex) => (
                      <button
                        key={`${rowIndex}-${colIndex}`}
                        onClick={() => handleCookieClick(rowIndex, colIndex)}
                        disabled={gameOver || !cookie || isBotThinking}
                        className={`
                          w-12 h-12 m-1 border-2 rounded-lg transition-all duration-200 text-2xl
                          ${
                            cookie
                              ? rowIndex === rows - 1 && colIndex === 0
                                ? "bg-red-400 border-red-600 hover:bg-red-500 cursor-pointer" // Poison cookie
                                : "bg-yellow-400 border-yellow-600 hover:bg-yellow-500 cursor-pointer hover:scale-110"
                              : "bg-gray-200 border-gray-300 cursor-not-allowed"
                          }
                          ${!gameOver && cookie && !isBotThinking && "hover:shadow-lg"}
                        `}
                        title={
                          cookie
                            ? rowIndex === rows - 1 && colIndex === 0
                              ? "Poison Cookie - Avoid!"
                              : "Click to chomp"
                            : "Already chomped"
                        }
                      >
                        {cookie
                          ? rowIndex === rows - 1 && colIndex === 0
                            ? "💀"
                            : "🍪"
                          : ""}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div className="text-sm text-gray-600 text-center">
              {!gameOver && (
                <p>
                  Click any cookie to chomp it and all cookies above and to the
                  right. Avoid the poison! 💀
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
